import { useEffect, useState } from 'react';
import {
  stockHistoryLogGetApi,
  StockHistoryLogRequestType,
  StockHistoryLogType,
  userListGetApi,
  UserType,
} from '../../../api/apiConfig';
import useFetch from '../../../api/useFetch';
import styled, { css } from 'styled-components';
import { InputUnderline } from '../../../component/input/InputUnderline';
import {
  formatEightDigitStringToSixDigitDot,
  formatEightDigitStringToSlashForm,
  getMonthStartEnd,
} from '../../../function/replaceString';
import {
  adjustEndDate,
  calculateMonthsDifference,
  isValidRange,
} from '../../../function/dateVerify';
import toast from 'react-hot-toast';
import { FiTrash2 } from 'react-icons/fi';
import theme from '../../../styles/theme';
import { BiWindowClose } from 'react-icons/bi';
import * as Styled from '../../anonymouse/pages/LoginPageStyled';
import { useHeaderButtons } from '../../../component/header/HeaderButtonProvider';
import SquareButton from '../../../component/button/SquareButton';
import { fa } from '@faker-js/faker/.';

// 하위 그룹 (ex: 날짜별 로그, 유저별 로그)
export type SubGroupType = {
  key: string; // 날짜 or 유저 ID
  logs: StockHistoryLogType[];
};

// 상위 그룹 (ex: 유저별 -> 날짜로 묶인 서브, 날짜별 -> 유저로 묶인 서브)
export type GroupedDataItemType = {
  key: string; // 유저 ID or 날짜
  subGroup: SubGroupType[];
};

export const StockHistoryLog = () => {
  const [periodStartDate, setPeriodStartDate] = useState<string>(
    getMonthStartEnd(new Date()).startDash
  );

  const [periodEndDate, setPeriodEndDate] = useState<string>(
    getMonthStartEnd(new Date()).endDash
  );

  const [syncTypeFilter, setSyncTypeFilter] = useState<
    'synced' | 'unsynced' | null
  >(null);

  const [sortMode, setSortMode] = useState<'worker' | 'date'>('worker');

  const [groupMode, setGroupMode] = useState<boolean>(true);

  const [hasProdItem, setHasProdItem] = useState<boolean>(false);

  const { setButtons } = useHeaderButtons();

  const [originLogs, setOriginLogs] = useState<StockHistoryLogType[]>([]);

  const [userDropdownOpen, setUserDropdownOpen] = useState<boolean>(false);

  const [userList, setUserList] = useState<UserType[]>([]);

  const [userSearchTerm, setUserSearchTerm] = useState<string>('');

  const [selectedUserList, setSelectedUserList] = useState<UserType[]>([]);

  const [showEach, setShowEach] = useState<boolean>(false);

  const { fetch: fetchUserListGetApi } = useFetch({
    fetchFunction: userListGetApi,
    onSuccess: (data: UserType[]) =>
      setUserList(data.filter((user) => user.role !== 'SUPER')),
  });

  const { fetch: fetchStockHistoryLogGetApi } = useFetch({
    fetchFunction: stockHistoryLogGetApi,
    onSuccess: (data: StockHistoryLogType[]) => {
      setOriginLogs(data);
      syncTypeFilter !== 'unsynced'
        ? setHasProdItem(true)
        : setHasProdItem(false);
    },
  });

  const [tabList, setTabList] = useState<string[]>([]);

  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  const [groupedData, setGroupedData] = useState<GroupedDataItemType[]>([]);

  useEffect(() => {
    //헤더에 버튼 추가
    setButtons(
      <>
        <SquareButton
          text='조회하기'
          colorType='ACTIVE'
          onClick={() => {
            if (selectedUserList.length < 1) return;
            const dto: StockHistoryLogRequestType = {
              userIds: selectedUserList.map((user) => user.id!),
              startDt: periodStartDate,
              endDt: periodEndDate,
              unsyncedFlag:
                syncTypeFilter === null
                  ? null
                  : syncTypeFilter === 'synced'
                  ? 'N'
                  : 'Y',
            };
            fetchStockHistoryLogGetApi(dto);
          }}
        />
      </>
    );
    // 페이지가 바뀌면 버튼 초기화
    return () => setButtons(null);
  }, [
    setButtons,
    periodStartDate,
    periodEndDate,
    selectedUserList,
    syncTypeFilter,
  ]);

  useEffect(() => {
    fetchUserListGetApi();
  }, []);

  useEffect(() => {
    if (originLogs.length < 1) return;

    // 동일 유저 + 제품 + 머신 식별자 + 머신 이름으로 그룹핑 후 수량 합산
    const groupLogs = (logs: StockHistoryLogType[]) => {
      const map: Record<string, StockHistoryLogType> = {};
      logs.forEach((log) => {
        const key = `${log.userId}-${log.prodCd}-${log.machineIdentifier}-${log.machineName}`;
        if (!map[key]) {
          map[key] = { ...log };
        } else {
          map[key].changedQuantity += log.changedQuantity;
        }
      });
      return Object.values(map);
    };

    // 작업자 기준 그룹핑
    const groupedByWorker = (logs: StockHistoryLogType[]) => {
      return selectedUserList.map((user) => {
        const userLogs = logs.filter((log) => log.userId === user.id);
        const dateMap: Record<string, StockHistoryLogType[]> = {};

        userLogs.forEach((log) => {
          if (!dateMap[log.date]) dateMap[log.date] = [];
          dateMap[log.date].push(log);
        });

        const subGroup = Object.entries(dateMap)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, dateLogs]) => ({
            key: date,
            logs: groupMode ? groupLogs(dateLogs) : dateLogs,
          }));

        return {
          key: user.id!.toString(),
          subGroup,
        };
      });
    };

    // 날짜 기준 그룹핑
    const groupedByDate = (logs: StockHistoryLogType[]) => {
      const dateMap: Record<string, StockHistoryLogType[]> = {};
      logs.forEach((log) => {
        if (!dateMap[log.date]) dateMap[log.date] = [];
        dateMap[log.date].push(log);
      });

      return Object.entries(dateMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, dateLogs]) => {
          const userMap: Record<string, StockHistoryLogType[]> = {};

          dateLogs.forEach((log) => {
            const key = log.userId.toString();
            if (!userMap[key]) userMap[key] = [];
            userMap[key].push(log);
          });

          const subGroup = Object.entries(userMap).map(([userId, logs]) => ({
            key: userId,
            logs: groupMode
              ? groupLogs(logs)
              : logs.sort((a, b) => a.userName.localeCompare(b.userName)),
          }));

          return {
            key: date,
            subGroup,
          };
        });
    };

    // 가공된 데이터
    const groupedData =
      sortMode === 'worker'
        ? groupedByWorker(originLogs)
        : groupedByDate(originLogs);

    // 탭 목록 구성 (날짜 또는 작업자 이름)
    const newTabList =
      sortMode === 'worker'
        ? selectedUserList.map((user) => user.userName)
        : Array.from(new Set(originLogs.map((log) => log.date))).sort();

    newTabList.length > 0 && setSelectedTab(newTabList[0]);
    setTabList(newTabList);
    setGroupedData(groupedData);
  }, [originLogs, sortMode, groupMode]);

  // 시작일 변경 처리
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;

    if (periodEndDate && !isValidRange(newStartDate, periodEndDate)) {
      return;
    }

    setPeriodStartDate(newStartDate);

    if (periodEndDate) {
      // 시작일 변경 시 종료일 조정
      const monthsDiff = calculateMonthsDifference(newStartDate, periodEndDate);
      if (monthsDiff >= 12) {
        // 종료일이 12개월을 초과하는 경우, 종료일을 12개월 내로 조정
        const adjustedEndDate = adjustEndDate(newStartDate);
        const newEndDateStr = adjustedEndDate.toISOString().split('T')[0];
        setPeriodEndDate(newEndDateStr);
        toast.error('기간이 12개월을 초과하여 자동으로 조정되었습니다');
      }
    }
  };

  // 종료일 변경 처리
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;

    if (periodStartDate && !isValidRange(periodStartDate, newEndDate)) {
      return;
    }

    setPeriodEndDate(newEndDate);

    // 종료일 변경 시 월 수가 12개월을 넘지 않도록 조정
    const monthsDiff = calculateMonthsDifference(periodStartDate, newEndDate);
    if (monthsDiff >= 12) {
      // 종료일이 12개월을 초과하는 경우, 종료일을 12개월 내로 조정
      const adjustedEndDate = adjustEndDate(periodStartDate);
      const newEndDateStr = adjustedEndDate.toISOString().split('T')[0];
      setPeriodEndDate(newEndDateStr);
      toast.error('기간이 12개월을 초과하여 자동으로 조정되었습니다');
    }
  };

  console.log('tablist', tabList);

  return (
    <>
      <h3 style={{ marginTop: 0 }}>검색 옵션</h3>
      <PeriodContainer>
        <PeriodRow>
          <RowLabel>조회시작일</RowLabel>
          <RowContent>
            <DateInput
              type='date'
              value={periodStartDate}
              onChange={handleStartDateChange}
            />
          </RowContent>
          <RowLabel>조회종료일</RowLabel>
          <RowContent>
            <DateInput
              type='date'
              value={periodEndDate}
              onChange={handleEndDateChange}
            />
          </RowContent>
        </PeriodRow>
        {/* <PeriodRow>
        <RowLabel>개별 이력 보기</RowLabel>
        <RowContent>
          <input
            type='checkbox'
            checked={showEach}
            onChange={(e) => setShowEach(e.target.checked)}
            style={{ width: '17px', height: '17px' }}
          />
        </RowContent>
      </PeriodRow> */}
        <PeriodRow>
          <RowLabel>타입 설정</RowLabel>
          <RowContent
            style={{ display: 'flex', flexDirection: 'row', gap: '15px' }}
          >
            <CheckboxArea onClick={() => setSyncTypeFilter(null)}>
              <input
                type='checkbox'
                checked={syncTypeFilter === null}
                style={{ width: '17px', height: '17px' }}
              />
              전체
            </CheckboxArea>
            <CheckboxArea onClick={() => setSyncTypeFilter('synced')}>
              <input
                type='checkbox'
                checked={syncTypeFilter === 'synced'}
                style={{ width: '17px', height: '17px' }}
              />
              자동소모목록만
            </CheckboxArea>
            <CheckboxArea onClick={() => setSyncTypeFilter('unsynced')}>
              <input
                type='checkbox'
                checked={syncTypeFilter === 'unsynced'}
                style={{ width: '17px', height: '17px' }}
              />
              수동소모목록만
            </CheckboxArea>
          </RowContent>
        </PeriodRow>
        <PeriodRow style={{ position: 'relative' }}>
          <RowLabel>작업자 선택</RowLabel>
          <RowContent>
            <div
              style={{
                cursor: 'pointer',
                width: 'fit-content',
                display: 'flex',
                flexDirection: 'row',
                alignContent: 'center',
              }}
            >
              <>
                <div
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  style={{
                    cursor: 'pointer',
                    minWidth: '80px',
                    textAlign: 'center',
                  }}
                >
                  추가하기
                </div>
                <SelectedProdItemContainer>
                  {selectedUserList.length > 0 &&
                    selectedUserList.map((user) => (
                      <SelectedProdItem key={user.id}>
                        <FiTrash2
                          size={15}
                          color={theme.colors.red}
                          style={{
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            setSelectedUserList((prev) =>
                              prev.filter((prevUser) => prevUser !== user)
                            )
                          }
                        />
                        <SelectedProdItem>{user.userName}</SelectedProdItem>
                      </SelectedProdItem>
                    ))}
                </SelectedProdItemContainer>
              </>

              <DropdownBase show={userDropdownOpen}>
                <ProductDropdownHeader>
                  <div
                    style={{
                      height: '26px',
                      backgroundColor: '#1f48d4',
                      marginBottom: '3px',
                      textAlign: 'end',
                      alignContent: 'center',
                      paddingRight: '5px',
                    }}
                  >
                    <BiWindowClose
                      size={20}
                      color='white'
                      style={{ cursor: 'pointer' }}
                      onClick={() => setUserDropdownOpen(false)}
                    />
                  </div>
                  <div style={{ padding: '3px', display: 'flex' }}>
                    <Styled.Input
                      placeholder='유저'
                      value={userSearchTerm}
                      style={{ marginBottom: '5px', maxHeight: '18px' }}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                    />
                  </div>
                </ProductDropdownHeader>
                <ProductDropdownBody>
                  {userList.length > 0 ? (
                    userList
                      .filter((user) =>
                        userSearchTerm?.trim()
                          ? user.userName.includes(userSearchTerm)
                          : true
                      )
                      .map((user) => (
                        <DropdownItem
                          key={user.id}
                          onClick={() => {
                            !selectedUserList.some(
                              (data) => data.id === user.id
                            ) && setSelectedUserList((prev) => [...prev, user]);
                          }}
                          included={selectedUserList.some(
                            (data) => data.id === user.id
                          )}
                        >
                          {user.userName}
                        </DropdownItem>
                      ))
                  ) : (
                    <div>작업장 정보가 없습니다</div>
                  )}
                </ProductDropdownBody>
              </DropdownBase>
            </div>
          </RowContent>
        </PeriodRow>
      </PeriodContainer>
      <PeriodContainer>
        <PeriodRow>
          <RowLabel>묶음 기준</RowLabel>
          <RowContent
            style={{ display: 'flex', flexDirection: 'row', gap: '15px' }}
          >
            <CheckboxArea onClick={() => setSortMode('worker')}>
              <input
                type='checkbox'
                checked={sortMode === 'worker'}
                style={{ width: '17px', height: '17px' }}
              />
              작업자별
            </CheckboxArea>
            <CheckboxArea onClick={() => setSortMode('date')}>
              <input
                type='checkbox'
                checked={sortMode === 'date'}
                style={{ width: '17px', height: '17px' }}
              />
              날짜별
            </CheckboxArea>
          </RowContent>
        </PeriodRow>
        <PeriodRow>
          <RowLabel>동일품목</RowLabel>
          <RowContent
            style={{ display: 'flex', flexDirection: 'row', gap: '15px' }}
          >
            <CheckboxArea onClick={() => setGroupMode(true)}>
              <input
                type='checkbox'
                checked={groupMode}
                style={{ width: '17px', height: '17px' }}
              />
              묶음
            </CheckboxArea>
            <CheckboxArea onClick={() => setGroupMode(false)}>
              <input
                type='checkbox'
                checked={!groupMode}
                style={{ width: '17px', height: '17px' }}
              />
              개별
            </CheckboxArea>
          </RowContent>
        </PeriodRow>
      </PeriodContainer>
      {tabList.length > 0 && (
        <TabSection>
          {tabList.map((tab, index) => (
            <TabSelect
              key={index}
              selected={selectedTab === tab}
              isFirstItem={index === 0}
              isLastItem={index + 1 === tabList.length}
              isFixed={false}
              onClick={() => setSelectedTab(tab)}
            >
              {typeof tab === 'string' && /^\d{8}$/.test(tab)
                ? formatEightDigitStringToSixDigitDot(tab)
                : tab}
            </TabSelect>
          ))}
        </TabSection>
      )}
      <TableContainer>
        <TableRow className='table-header'>
          {sortMode === 'worker' ? (
            <div className='date'>날짜</div>
          ) : (
            <div className='name'>작업자명</div>
          )}
          <div className='prod-des'>품목명</div>
          <div className='qty'>변동수량</div>
          <div className='unit'>단위</div>
          <div className='machine-name'>기계명</div>
          <div className='workbay'>작업장</div>
          {hasProdItem && <div className='produced'>생산된 품목명</div>}
        </TableRow>
        {(() => {
          const tabKey =
            sortMode === 'worker'
              ? selectedUserList
                  .find((user) => user.userName === selectedTab)
                  ?.id!.toString()
              : selectedTab;
          const group = groupedData.find((group) => group.key === tabKey);
          return group?.subGroup.map((subGroup) => (
            <>
              <AssignmentDateRow>
                <div
                  className={sortMode === 'worker' ? 'date' : 'name'}
                  style={{
                    flex: 1,
                    textAlign: 'start',
                    paddingLeft: '15px',
                    fontWeight: 700,
                  }}
                >
                  {sortMode === 'date'
                    ? selectedUserList.find(
                        (user) => user.id!.toString() === subGroup.key
                      )?.userName
                    : formatEightDigitStringToSlashForm(subGroup.key)}
                </div>
              </AssignmentDateRow>
              {subGroup.logs.map((log, subIndex) => (
                <ProductDescRow isLastItem={false}>
                  <div className={sortMode === 'worker' ? 'date' : 'name'} />
                  <div className='prod-des'>{log.prodDes}</div>
                  <div className='qty'>
                    {Number.isInteger(log.changedQuantity)
                      ? log.changedQuantity.toString()
                      : log.changedQuantity.toFixed(3).replace(/\.?0+$/, '')}
                  </div>
                  <div className='unit'>{log.unit || '-'}</div>
                  <div className='machine-name'>{log.machineName}</div>
                  <div className='workbay'>{log.workbayName}</div>
                  {hasProdItem && (
                    <div className='produced'>{log.producedDes || ''}</div>
                  )}
                </ProductDescRow>
              ))}
              {}
            </>
          ));
        })()}
      </TableContainer>
    </>
  );
};

const PeriodContainer = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const PeriodRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-items: stretch;

  &:not(:last-child) {
    border-bottom: 1px solid #b8b8b8;
  }
`;
const RowLabel = styled.div`
  flex: 0 0 120px;
  background-color: #d9d9d9;
  font-size: 15px;
  align-content: center;
  text-align: center;
  padding: 4px 3px;
  font-weight: 700;
`;

const RowContent = styled.div`
  flex-grow: 1;
  padding: 4px 10px;
  background-color: white;
`;

const DateInput = styled.input`
  width: 100%;
  color: #595b60;
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  box-sizing: border-box;
  border: none;
  cursor: pointer;
`;

const DropdownBase = styled.div<{ show: boolean }>`
  font-weight: 500;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  overflow: hidden;
  position: absolute;
  background-color: white;
  left: 100%;
  margin-left: 15px;
  top: 0px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(71, 71, 71, 0.678);
  transform: ${({ show }) => (show ? 'scale(1)' : 'scale(0)')};
  transform-origin: top left;
  transition: transform 0.3s ease, width 0.3s ease, height 0.3s ease;
`;
const DropdownItem = styled.div<{ included: boolean }>`
  width: 100%;
  padding: 4px 1px;
  ${({ included }) =>
    included
      ? css`
          background-color: ${({ theme }) => theme.colors.lime};
        `
      : css`
          cursor: pointer;
          &:hover {
            background-color: ${({ theme }) => theme.colors.lightGray};
          }
        `}
`;

const ProductDropdownHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ProductDropdownBody = styled.div`
  position: relative;
  gap: 6px;
  margin: 7px;
  display: flex;
  flex-direction: column;
  width: 220px;
  overflow-x: hidden;
  overflow-y: scroll;
  min-height: 350px;
  max-height: 500px;
  background-color: ${({ theme }) => theme.colors.whiteGray};
`;

const SelectedProdItemContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const SelectedProdItem = styled.div`
  display: flex;
  padding: 0px 4px;
  flex-direction: row;
  gap: 3px;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
`;

const CheckboxArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 3px;
`;

const TabSection = styled.div`
  display: flex;
  margin-top: 15px;
  flex-direction: row;
  align-items: center;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TabSelect = styled.div<{
  selected: boolean;
  isFirstItem: boolean;
  isLastItem: boolean;
  isFixed: boolean;
}>`
  cursor: pointer;

  padding: 8px 12px;
  font-weight: 600;
  border-bottom: ${({ isFixed, theme }) =>
    isFixed ? '1px solid transparent' : `1px solid ${theme.colors.red}`};
  align-content: center;
  text-align: center;
  background-color: ${({ selected, theme }) =>
    selected ? theme.colors.white : theme.colors.white_odd};

  border-top-left-radius: ${({ isFirstItem }) => (isFirstItem ? '15px' : 0)};
  border-top-right-radius: ${({ isLastItem }) => (isLastItem ? '15px' : 0)};

  &:hover {
    background-color: rgb(255 255 255 / 4%);
  }
`;

const TableContainer = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontsize[15]};
  color: ${({ theme }) => theme.colors.charcoal};

  .table-header {
    font-size: ${({ theme }) => theme.fontsize[18]};
    border-bottom: 1px solid ${({ theme }) => theme.colors.charcoal};
    font-weight: 600;
  }

  .date {
    flex: 0 0 110px;
  }
  .name {
    flex: 0 0 80px;
  }

  .machine-name {
    flex: 0 0 120px;
  }

  .unit {
    flex: 0 0 80px;
  }

  .prod-des {
    flex: 0 0 240px;
  }
  .qty {
    flex: 0 0 130px;
  }

  .workbay {
    flex: 0 0 260px;
  }

  .produced {
    flex: 1;
  }

  * {
    align-content: center;
    text-align: center;
    box-sizing: border-box;
  }
`;

const TableRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  max-height: 30px;
  *:not(svg) {
    min-height: 30px;
    max-height: 30px;
  }
`;

const AssignmentDateRow = styled(TableRow)`
  height: 30px;
  /* background-color: #d0d0d0a8; */
  color: #0031ab;

  .time-date {
  }
  .manager {
    flex: 0 0 95px;
  }
  .prod-des {
    flex: 0 1 17%;
  }
  .cust-name {
    flex: 0 1 17%;
  }
  .qty {
    flex: 0 0 70px;
  }
  .requirement {
    flex: 0 1 25%;
  }
  .address {
    flex: 0 1 35%;
  }

  * {
    flex: 1;
  }
`;

const SubGroupArea = styled.div`
  width: 100%;
`;

const ProductDescRow = styled(TableRow)<{ isLastItem: boolean }>`
  .prod-des,
  .cust-name,
  .requirement {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .requirement {
    padding: 0px 15px;
  }
  ${({ isLastItem }) =>
    isLastItem &&
    css`
      .time-date,
      .prod-des,
      .cust-name,
      .qty,
      .requirement,
      .address {
        border-bottom: 1px solid ${({ theme }) => theme.colors.wine};
      }

      *:not(svg) {
        width: 100%;
        height: 100%;
      }
    `}
`;
